import React from 'react';
import styled from '@emotion/styled';
import { Box, IconButton, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PublicIcon from '@mui/icons-material/Public';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PermIdentity from '@mui/icons-material/PermIdentity';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setStep } from '../santeMobiliteSlice';

/**
 * Stepper header responsive : on garde les 5 étapes sur UNE seule ligne en
 * mobile (no-wrap) pour éviter le découpage 3+2 disgracieux. Pour que ça passe
 * dans la largeur écran, on réduit en cascade les tailles des icones, lignes
 * de séparation et labels en dessous de 600px.
 */
const StepContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  flex-wrap: nowrap;
  @media (max-width: 600px) {
    margin-top: 0.5rem;
    padding: 0 4px;
  }
`;

const Step = styled(Box)<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ $active }) => ($active ? '#8CCDCD' : '#b0bec5')};
  flex-shrink: 0;
`;

const StepLine = styled(Box)<{ $active?: boolean }>`
  width: 36px;
  height: 2px;
  background-color: ${({ $active }) => ($active ? '#8CCDCD' : '#b0bec5')};
  margin: 0 8px;
  flex-shrink: 1;
  min-width: 8px;
  @media (max-width: 600px) {
    width: 14px;
    margin: 0 2px;
  }
`;

const IconButtonStyled = styled(IconButton)<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? '#8CCDCD' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : '#8CCDCD')};
  border: ${({ $active }) => ($active ? '0px' : 'solid 1px #8CCDCD')};
  @media (max-width: 600px) {
    padding: 6px;
    & .MuiSvgIcon-root {
      font-size: 18px;
    }
  }
`;

const StepLabel = styled(Typography)`
  font-size: 0.75rem;
  margin-top: 2px;
  text-align: center;
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 0.65rem;
    margin-top: 1px;
  }
`;

const STEPS: Array<{ idx: number; icon: React.ElementType; key: string }> = [
  { idx: 1, icon: CalendarTodayIcon, key: 'layout.step_dates' },
  { idx: 2, icon: PublicIcon, key: 'layout.step_destination' },
  { idx: 3, icon: HealthAndSafetyIcon, key: 'layout.step_social_security' },
  { idx: 4, icon: PermIdentity, key: 'layout.step_info' },
  { idx: 5, icon: DescriptionIcon, key: 'layout.step_finalization' },
];

const SanteMobiliteStepperHeader: React.FC = () => {
  const { t } = useTranslation('sante-mobilite');
  const dispatch = useAppDispatch();
  const step = useAppSelector(state => state.santeMobilite.step);
  const lastVerifiedStep = useAppSelector(state => state.santeMobilite.lastVerifiedStep);

  const goTo = (target: number) => {
    if (target > lastVerifiedStep + 1) return;
    // On dispatch direct le step dans Redux ; SanteMobilitePage propage ensuite
    // la valeur à l'URL via son useEffect [step] qui préserve les autres query
    // params (?school=, etc.) avec params.toString().
    dispatch(setStep(target));
  };

  return (
    <StepContainer>
      {STEPS.map(({ idx, icon: Icon, key }, i) => (
        <React.Fragment key={idx}>
          {i > 0 && <StepLine $active={step >= idx} />}
          <Step $active={step >= idx}>
            <IconButtonStyled
              $active={step >= idx}
              onClick={() => goTo(idx)}
              sx={{ cursor: idx <= lastVerifiedStep + 1 ? 'pointer' : 'default' }}
            >
              <Icon />
            </IconButtonStyled>
            <StepLabel variant="caption">{t(key)}</StepLabel>
          </Step>
        </React.Fragment>
      ))}
    </StepContainer>
  );
};

export default SanteMobiliteStepperHeader;
